import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/callout/callout.js';
import '@awesome.me/webawesome/dist/components/dialog/dialog.js';
import '@awesome.me/webawesome/dist/components/input/input.js';
import '@awesome.me/webawesome/dist/components/option/option.js';
import '@awesome.me/webawesome/dist/components/select/select.js';
import '@awesome.me/webawesome/dist/components/spinner/spinner.js';
import '@awesome.me/webawesome/dist/components/switch/switch.js';
import '@awesome.me/webawesome/dist/components/tag/tag.js';

import { ApiError, api, formValues, invoiceStatusLabels, monthKey, type Client, type Invoice, type InvoiceDetail, type InvoiceStatus } from '../api.js';
import { navigate, replaceUrl } from '../router.js';
import { pageStyles } from '../shared-styles.js';

const statusVariant: Record<InvoiceStatus, 'neutral' | 'success' | 'warning'> = { draft: 'warning', issued: 'success', archived: 'neutral' };

/** Invoice list with filters and the "New invoice" dialog. */
@customElement('invoices-page')
export class InvoicesPage extends LitElement {
  static styles = pageStyles;

  @state() private items: Invoice[] = [];
  @state() private clients: Client[] = [];
  @state() private clientId = '';
  @state() private year = String(new Date().getFullYear());
  @state() private status = '';
  @state() private includeArchived = false;
  @state() private loading = true;
  @state() private error = '';

  @state() private dialogOpen = false;
  @state() private creating = false;
  @state() private formError = '';

  connectedCallback() {
    super.connectedCallback();
    const p = new URLSearchParams(window.location.search);
    this.clientId = p.get('client_id') ?? '';
    this.year = p.get('year') ?? this.year;
    this.status = p.get('status') ?? '';
    this.includeArchived = p.get('archived') === '1';
    void this.init();
  }

  private async init() {
    try {
      this.clients = await api.get<Client[]>('/api/clients');
    } catch (e) {
      this.error = (e as Error).message;
    }
    await this.load();
  }

  private async load() {
    const params = new URLSearchParams();
    if (this.clientId) params.set('client_id', this.clientId);
    if (this.year) params.set('year', this.year);
    if (this.status) params.set('status', this.status);
    if (this.includeArchived) params.set('include_archived', '1');
    const url = new URLSearchParams(params);
    url.delete('include_archived');
    if (this.includeArchived) url.set('archived', '1');
    replaceUrl(`/invoices${url.size ? `?${url}` : ''}`);
    this.loading = true;
    this.error = '';
    try {
      this.items = await api.get<Invoice[]>(`/api/invoices?${params}`);
    } catch (e) {
      this.error = (e as Error).message;
    } finally {
      this.loading = false;
    }
  }

  private async create(e: Event) {
    e.preventDefault();
    const v = formValues(e.target as HTMLFormElement);
    this.creating = true;
    this.formError = '';
    try {
      const d = await api.post<InvoiceDetail>('/api/invoices', { client_id: Number(v.client_id), month: v.month });
      this.dialogOpen = false;
      navigate(`/invoices/${d.id}`);
    } catch (err) {
      if (err instanceof ApiError) {
        const hint = Object.values(err.fields)[0];
        this.formError = hint ? `${err.message}: ${hint}` : err.message;
      } else {
        this.formError = (err as Error).message;
      }
    } finally {
      this.creating = false;
    }
  }

  private onDialogHidden = (e: Event) => {
    if (e.target === e.currentTarget) this.dialogOpen = false;
  };

  render() {
    return html`
      <div class="page-header">
        <h2>Invoices</h2>
        <span class="spacer"></span>
        <wa-button variant="brand" @click=${() => { this.formError = ''; this.dialogOpen = true; }}>New invoice</wa-button>
      </div>
      <div class="toolbar">
        <wa-select label="Client" .value=${this.clientId} @change=${(e: Event) => { this.clientId = (e.target as HTMLSelectElement).value; void this.load(); }}>
          <wa-option value="">All clients</wa-option>
          ${this.clients.map((c) => html`<wa-option value=${String(c.id)}>${c.name}</wa-option>`)}
        </wa-select>
        <wa-input label="Year" type="number" min="2000" max="2200" .value=${this.year} @change=${(e: Event) => { this.year = (e.target as HTMLInputElement).value; void this.load(); }}></wa-input>
        <wa-select label="Status" .value=${this.status} @change=${(e: Event) => { this.status = (e.target as HTMLSelectElement).value; void this.load(); }}>
          <wa-option value="">Any</wa-option>
          ${(Object.keys(invoiceStatusLabels) as InvoiceStatus[]).map((s) => html`<wa-option value=${s}>${invoiceStatusLabels[s]}</wa-option>`)}
        </wa-select>
        <wa-switch ?checked=${this.includeArchived} @change=${(e: Event) => { this.includeArchived = (e.target as HTMLInputElement).checked; void this.load(); }}>Show archived</wa-switch>
      </div>
      ${this.error ? html`<wa-callout variant="danger">${this.error}</wa-callout>` : nothing}
      ${this.renderTable()} ${this.renderDialog()}
    `;
  }

  private renderTable() {
    if (this.loading) return html`<wa-spinner></wa-spinner>`;
    if (this.items.length === 0) return html`<div class="empty">No invoices match.</div>`;
    return html`
      <div class="table-wrap"><table class="cards">
        <thead>
          <tr>
            <th>Number</th>
            <th>Client</th>
            <th>Period</th>
            <th>Invoice date</th>
            <th>Due</th>
            <th class="num">Total</th>
            <th>Status</th>
            <th>PDF</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          ${this.items.map(
            (i) => html`
              <tr>
                <td data-label="No.">${i.number ?? html`<span class="muted">draft</span>`}</td>
                <td data-label="Client">${i.client_name}</td>
                <td data-label="Period">${i.period_from.slice(0, 7)}${i.period_to.slice(0, 7) !== i.period_from.slice(0, 7) ? ` – ${i.period_to.slice(0, 7)}` : ''}</td>
                <td class="date m-hide" data-label="Date">${i.invoice_date}</td>
                <td class="date" data-label="Due">${i.due_date}</td>
                <td class="num" data-label="Total">${i.total} ${i.currency}</td>
                <td data-label="Status"><wa-tag size="small" variant=${statusVariant[i.status]}>${invoiceStatusLabels[i.status]}</wa-tag>${i.ujp_status === 'registered' ? html` <wa-tag size="small">UJP</wa-tag>` : nothing}</td>
                <td class=${i.document_id ? '' : 'empty-cell'} data-label="PDF">${i.document_id ? html`<a href="/api/documents/${i.document_id}/download" target="_blank" rel="noopener">PDF</a>` : html`<span class="muted">—</span>`}</td>
                <td class="actions"><wa-button size="small" appearance="plain" @click=${() => navigate(`/invoices/${i.id}`)}>Open</wa-button></td>
              </tr>
            `,
          )}
        </tbody>
      </table></div>
    `;
  }

  private renderDialog() {
    const lastMonth = new Date();
    lastMonth.setDate(1);
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    return html`
      <wa-dialog label="New invoice" ?open=${this.dialogOpen} @wa-after-hide=${this.onDialogHidden} style="--width: min(30rem, calc(100vw - 1.5rem));">
        <form id="new-invoice" @submit=${this.create}>
          <wa-select class="full" name="client_id" label="Client" required .value=${this.clientId || (this.clients.length === 1 ? String(this.clients[0].id) : '')}>
            ${this.clients.map((c) => html`<wa-option value=${String(c.id)}>${c.name}</wa-option>`)}
          </wa-select>
          <wa-input class="full" name="month" label="Billing month" type="month" required .value=${monthKey(lastMonth)} hint="A draft is built from the imported time entries of this month"></wa-input>
          ${this.formError ? html`<wa-callout class="form-error" variant="danger">${this.formError}</wa-callout>` : nothing}
        </form>
        <div slot="footer" class="footer">
          <wa-button appearance="outlined" @click=${() => (this.dialogOpen = false)}>Cancel</wa-button>
          <wa-button variant="brand" type="submit" form="new-invoice" ?loading=${this.creating}>Create draft</wa-button>
        </div>
      </wa-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'invoices-page': InvoicesPage;
  }
}
